import React, { useState, useRef } from "react";
import "react-html5-camera-photo/build/css/index.css";
import "./imagePhotos.css";
import { FiCamera } from "react-icons/fi";
import { FaRegImages } from "react-icons/fa6";

import { canvasPreview } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop, convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

interface props {
	image: string;
	setImage: (e: string) => void;
	setSpinner: (e: boolean) => void;
}

interface viewProps {
	height: number;
	width: number;
	x: number;
	y: number;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
	return centerCrop(
		makeAspectCrop(
			{
				unit: "%",
				width: 90,
			},
			aspect,
			mediaWidth,
			mediaHeight
		),
		mediaWidth,
		mediaHeight
	);
}
const ImagePhotos = ({ image, setImage, setSpinner }: props) => {
	const [imageEditing, setImageEditing] = useState<boolean>(false);
	const [imageToEditing, setImageToEditing] = useState<string>();
	const [crop, setCrop] = useState<Crop>();
	const imgRef = useRef<HTMLImageElement>(null);
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const blobUrlRef = useRef("");

	const [imgSrc, setImgSrc] = useState("");
	const [scale, setScale] = useState(3 / 3);

	const [positionImage] = useState<viewProps>();

	const openGalery = useRef<HTMLInputElement>(null);
	const openGaleryCamera = useRef<HTMLInputElement>(null);

	function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
		const { width, height } = e.currentTarget;
		setCrop(centerAspectCrop(width, height, scale));
		setCompletedCrop(convertToPixelCrop(centerAspectCrop(width, height, scale), width, height));
	}

	async function onDownloadCropClick() {
		try {
			setSpinner(true);
			const image = imgRef.current;
			const previewCanvas = previewCanvasRef.current;
			if (!image || !previewCanvas || !completedCrop) {
				return;
			}

			const scaleX = image.naturalWidth / image.width;
			const scaleY = image.naturalHeight / image.height;

			const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);
			const ctx = offscreen.getContext("2d");
			if (!ctx) {
				return;
			}

			ctx.drawImage(previewCanvas, 0, 0, previewCanvas.width, previewCanvas.height, 0, 0, offscreen.width, offscreen.height);

			const blob = await offscreen.convertToBlob({
				type: "image/png",
			});

			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
			}
			blobUrlRef.current = URL.createObjectURL(blob);
			setImage(URL.createObjectURL(blob));
			setImageEditing(false);
		} catch (error) {
			console.log(error);
		} finally {
			setSpinner(false);
		}
	}

	useDebounceEffect(
		async () => {
			if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
				// We use canvasPreview as it's much faster than imgPreview.
				canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale);
			}
		},
		100,
		[completedCrop, scale]
	);
	function handleToggleAspectClick() {
		if (imgRef.current) {
			const { width, height } = imgRef.current;
			const newCrop = centerAspectCrop(width, height, 3 / 3);
			setCrop(newCrop);
			// Updates the preview
			setCompletedCrop(convertToPixelCrop(newCrop, width, height));
		}
	}
	openGalery.current?.addEventListener("cancel", () => {
		setImageEditing(false);
		setImageToEditing("");
	});

	return (
		<>
			<input
				type="file"
				aria-label={"cos"}
				accept="image/*, ratio=3/3"
				ref={openGalery}
				style={{ display: "none" }}
				size={4}
				onClick={(e) => {
					setImageEditing(true);
					setImageToEditing("");
				}}
				onChange={(e) => {
					if (e.target.files) {
						setImageToEditing(URL.createObjectURL(e.target.files[0]));
					}
				}}
			/>
			<input
				type="file"
				aria-label={"cos"}
				accept="image/* , editing=true, ratio=3/3"
				capture="user"
				ref={openGaleryCamera}
				style={{ display: "none" }}
				size={4}
				onClick={(e) => {
					setImageEditing(true);
					setImageToEditing("");
				}}
				onChange={(e) => {
					if (e.target.files) {
						setImageToEditing(URL.createObjectURL(e.target.files[0]));
					}
				}}
			/>
			<div className="imagePhotosContainer">
				<FaRegImages
					className="iconImage"
					name="image"
					size={30}
					color="blue"
					style={positionImage && { position: "absolute", top: positionImage.y + positionImage.height / 2 - 15, left: positionImage.width - 50 }}
					onClick={() => openGalery.current?.click()}
				></FaRegImages>
				<div className="imagePhotosView">
					<div onClick={() => openGalery.current?.click()}>
						<img srcSet={image} className="imagePhotosImage" alt="" />
					</div>
				</div>
				<FiCamera
					className="iconImage"
					name="camera"
					size={30}
					color="blue"
					style={positionImage && { position: "absolute", top: positionImage.y + positionImage.height / 2 - 15, left: positionImage.width - 50 }}
					onClick={() => openGaleryCamera.current?.click()}
				></FiCamera>
				{imageEditing && imageToEditing && (
					<>
						<div className="imageEditingContainer">
							<div className="imageEditingViewContainer">
								<span className="imageEditingViewContainerText"> Wybierz fragment zdjęcia</span>
								<ReactCrop
									className="imageEditingCrop"
									crop={crop}
									onChange={(_, percentCrop) => {
										setCrop(percentCrop);
									}}
									onComplete={(c) => setCompletedCrop(c)}
									aspect={scale}
								>
									<img alt={"zdjecie"} srcSet={imageToEditing} ref={imgRef} onLoad={onImageLoad} style={{ width: "100%", height: "100%" }} />
								</ReactCrop>
								<div className="imageEditingCropButtons">
									<span className={"imageEditingSave"} onClick={onDownloadCropClick}>
										ZAPISZ
									</span>
									<span className={"imageEditingAnuluj"} onClick={() => setImageEditing(false)}>
										Anuluj
									</span>
								</div>
							</div>
							<div>
								<canvas
									ref={previewCanvasRef}
									style={{
										border: "1px solid black",
										objectFit: "contain",
										width: completedCrop?.width,
										height: completedCrop?.height,
										display: "none",
									}}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default ImagePhotos;
